/**
 * WordGameBoard Component
 *
 * Renders the 15x15 game board with:
 * - Color-coded premium squares
 * - Tile placement via click-to-select, click-to-place
 * - Player rack with tile slots
 * - Action buttons (Play, Shuffle, Exchange, Pass)
 * - Last word highlighting
 */

import React, { useState, useCallback } from 'react';
import {
  WordGameState,
  BoardCell,
  Tile,
  TilePlacement,
  PremiumSquareType,
} from '../types';
import './WordGameBoard.css';

interface WordGameBoardProps {
  gameState: WordGameState;
  onPlayWord: (placements: TilePlacement[]) => void;
  onExchangeTiles: (tileIds: string[]) => void;
  onPassTurn: () => void;
  disabled: boolean;
  currentPlayerIndex: number;
}

const WordGameBoard: React.FC<WordGameBoardProps> = ({
  gameState,
  onPlayWord,
  onExchangeTiles,
  onPassTurn,
  disabled,
  currentPlayerIndex,
}) => {
  const [selectedTile, setSelectedTile] = useState<Tile | null>(null);
  const [pendingPlacements, setPendingPlacements] = useState<TilePlacement[]>([]);
  const [exchangeMode, setExchangeMode] = useState(false);
  const [selectedForExchange, setSelectedForExchange] = useState<Set<string>>(new Set());
  const [blankLetterPrompt, setBlankLetterPrompt] = useState<{ tile: Tile; row: number; col: number } | null>(null);

  const currentPlayer = gameState.players[currentPlayerIndex];
  const availableRack = currentPlayer.rack.filter(
    t => !pendingPlacements.some(p => p.tile.id === t.id)
  );

  const handleCellClick = useCallback((row: number, col: number) => {
    if (disabled || exchangeMode) return;

    // If there's a pending placement on this cell, remove it
    const existingIdx = pendingPlacements.findIndex(p => p.row === row && p.col === col);
    if (existingIdx !== -1) {
      const removed = pendingPlacements[existingIdx];
      setPendingPlacements(prev => prev.filter((_, i) => i !== existingIdx));
      setSelectedTile(null);
      return;
    }

    // If a tile is selected and the cell is empty
    if (selectedTile && !gameState.board[row][col].tile) {
      if (selectedTile.isBlank && !selectedTile.letter) {
        // Prompt for blank tile letter assignment
        setBlankLetterPrompt({ tile: selectedTile, row, col });
        return;
      }

      setPendingPlacements(prev => [...prev, { tile: selectedTile, row, col }]);
      setSelectedTile(null);
    }
  }, [disabled, exchangeMode, selectedTile, gameState.board, pendingPlacements]);

  const handleRackTileClick = useCallback((tile: Tile) => {
    if (disabled) return;

    if (exchangeMode) {
      setSelectedForExchange(prev => {
        const next = new Set(prev);
        if (next.has(tile.id)) next.delete(tile.id);
        else next.add(tile.id);
        return next;
      });
      return;
    }

    // Toggle selection
    if (selectedTile?.id === tile.id) {
      setSelectedTile(null);
    } else {
      setSelectedTile(tile);
    }
  }, [disabled, exchangeMode, selectedTile]);

  const handleBlankLetterSubmit = useCallback((letter: string) => {
    if (!blankLetterPrompt) return;
    const { tile, row, col } = blankLetterPrompt;
    const assignedTile = { ...tile, letter: letter.toUpperCase() };
    setPendingPlacements(prev => [...prev, { tile: assignedTile, row, col }]);
    setSelectedTile(null);
    setBlankLetterPrompt(null);
  }, [blankLetterPrompt]);

  const handleSubmitWord = useCallback(() => {
    if (pendingPlacements.length === 0) return;
    onPlayWord(pendingPlacements);
    setPendingPlacements([]);
    setSelectedTile(null);
  }, [pendingPlacements, onPlayWord]);

  const handleRecallTiles = useCallback(() => {
    setPendingPlacements([]);
    setSelectedTile(null);
  }, []);

  const handleShuffleRack = useCallback(() => {
    // Shuffle is visual only - handled by re-rendering with shuffled array
    // The actual rack order is managed by the engine
  }, []);

  const handleExchangeConfirm = useCallback(() => {
    if (selectedForExchange.size === 0) return;
    onExchangeTiles([...selectedForExchange]);
    setExchangeMode(false);
    setSelectedForExchange(new Set());
  }, [selectedForExchange, onExchangeTiles]);

  const handleExchangeCancel = useCallback(() => {
    setExchangeMode(false);
    setSelectedForExchange(new Set());
  }, []);

  const getPremiumLabel = (premium: PremiumSquareType): string => {
    switch (premium) {
      case PremiumSquareType.TRIPLE_WORD: return 'TW';
      case PremiumSquareType.DOUBLE_WORD: return 'DW';
      case PremiumSquareType.TRIPLE_LETTER: return 'TL';
      case PremiumSquareType.DOUBLE_LETTER: return 'DL';
      case PremiumSquareType.CENTER: return '\u2605';
      default: return '';
    }
  };

  const getCellClassName = (cell: BoardCell): string => {
    const classes = ['wgb__cell'];
    if (cell.tile) classes.push('wgb__cell--filled');
    if (pendingPlacements.some(p => p.row === cell.row && p.col === cell.col)) {
      classes.push('wgb__cell--pending');
    }
    if (gameState.lastPlacedPositions.some(pos => pos.row === cell.row && pos.col === cell.col)) {
      classes.push('wgb__cell--last-played');
    }

    switch (cell.premium) {
      case PremiumSquareType.TRIPLE_WORD: classes.push('wgb__cell--tw'); break;
      case PremiumSquareType.DOUBLE_WORD: classes.push('wgb__cell--dw'); break;
      case PremiumSquareType.TRIPLE_LETTER: classes.push('wgb__cell--tl'); break;
      case PremiumSquareType.DOUBLE_LETTER: classes.push('wgb__cell--dl'); break;
      case PremiumSquareType.CENTER: classes.push('wgb__cell--center'); break;
    }

    return classes.join(' ');
  };

  const renderTile = (tile: Tile, isPending?: boolean) => (
    <div className={`wgb__tile ${isPending ? 'wgb__tile--pending' : ''}`}>
      <span className="wgb__tile-letter">{tile.isBlank && !tile.letter ? '?' : tile.letter}</span>
      <span className="wgb__tile-value">{tile.value}</span>
    </div>
  );

  const getPendingTileAt = (row: number, col: number): TilePlacement | undefined => {
    return pendingPlacements.find(p => p.row === row && p.col === col);
  };

  return (
    <div className="wgb">
      {/* Board Grid */}
      <div className="wgb__board">
        {gameState.board.map((row, rowIdx) => (
          <div key={rowIdx} className="wgb__row">
            {row.map((cell, colIdx) => {
              const pending = getPendingTileAt(rowIdx, colIdx);
              return (
                <div
                  key={`${rowIdx}-${colIdx}`}
                  className={getCellClassName(cell)}
                  onClick={() => handleCellClick(rowIdx, colIdx)}
                >
                  {cell.tile ? (
                    renderTile(cell.tile)
                  ) : pending ? (
                    renderTile(pending.tile, true)
                  ) : (
                    <span className="wgb__cell-label">{getPremiumLabel(cell.premium)}</span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Player Rack */}
      <div className="wgb__rack-section">
        <div className="wgb__rack">
          {currentPlayer.rack.map((tile) => {
            const isUsed = pendingPlacements.some(p => p.tile.id === tile.id);
            const isSelected = selectedTile?.id === tile.id;
            const isForExchange = selectedForExchange.has(tile.id);
            return (
              <div
                key={tile.id}
                className={`wgb__rack-tile ${isUsed ? 'wgb__rack-tile--used' : ''} ${isSelected ? 'wgb__rack-tile--selected' : ''} ${isForExchange ? 'wgb__rack-tile--exchange' : ''}`}
                onClick={() => !isUsed && handleRackTileClick(tile)}
              >
                <span className="wgb__tile-letter">{tile.isBlank ? '?' : tile.letter}</span>
                <span className="wgb__tile-value">{tile.value}</span>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="wgb__actions">
          {!exchangeMode ? (
            <>
              <button
                className="wgb__btn wgb__btn--play"
                disabled={disabled || pendingPlacements.length === 0}
                onClick={handleSubmitWord}
              >
                Play Word
              </button>
              <button
                className="wgb__btn wgb__btn--recall"
                disabled={disabled || pendingPlacements.length === 0}
                onClick={handleRecallTiles}
              >
                Recall
              </button>
              <button
                className="wgb__btn wgb__btn--exchange"
                disabled={disabled || gameState.tileBag.length === 0}
                onClick={() => { setExchangeMode(true); setPendingPlacements([]); setSelectedTile(null); }}
              >
                Exchange
              </button>
              <button
                className="wgb__btn wgb__btn--pass"
                disabled={disabled}
                onClick={onPassTurn}
              >
                Pass
              </button>
            </>
          ) : (
            <>
              <button
                className="wgb__btn wgb__btn--play"
                disabled={selectedForExchange.size === 0}
                onClick={handleExchangeConfirm}
              >
                Confirm Exchange ({selectedForExchange.size})
              </button>
              <button
                className="wgb__btn wgb__btn--recall"
                onClick={handleExchangeCancel}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Blank Tile Letter Prompt */}
      {blankLetterPrompt && (
        <div className="wgb__blank-prompt-overlay">
          <div className="wgb__blank-prompt">
            <h3>Choose a letter for blank tile:</h3>
            <div className="wgb__blank-letters">
              {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(letter => (
                <button
                  key={letter}
                  className="wgb__blank-letter-btn"
                  onClick={() => handleBlankLetterSubmit(letter)}
                >
                  {letter}
                </button>
              ))}
            </div>
            <button
              className="wgb__btn wgb__btn--recall"
              onClick={() => { setBlankLetterPrompt(null); setSelectedTile(null); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WordGameBoard;
